import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges, Signal, SimpleChanges, computed, inject, input, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

import { OptionGroupType } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { EventModel, EventOption, EventOptionGroup, EventParticipantType } from '@frontend/sports/betting-offer/feature/model';
import { EpcotConfig, MarketGroupingConfig, Sitecore, SitecoreEventDetailsConfig } from '@frontend/sports/common/client-config-data-access';
import { ShowInEpcotDirective } from '@frontend/sports/common/core/feature/show-in-epcot';
import { Nullable, StringDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { interpolateSingleBracesString } from '@frontend/sports/common/core/utils/string';
import { SourceOfPick } from '@frontend/sports/option/pick/feature/tracking';
import { NOT_APPLICABLE, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { RxStrategyProvider } from '@rx-angular/cdk/render-strategies';
import { Promise as UnpatchedPromise } from '@rx-angular/cdk/zone-less/browser';
import { RxFor } from '@rx-angular/template/for';
import { BehaviorSubject, Subject, map, of, startWith, switchMap, take, tap } from 'rxjs';

import { CorrectScoreOptionPipe } from './correct-score-option.pipe';
import { CorrectScoreService } from './correct-score.service';
import { MarketHelpComponent } from './market-help/market-help.component';
import { OptionComponent } from './option.component';
import { USCorrectScoreService } from './us-correct-score.service';

@Component({
    selector: 'ms-correct-score-option-group',
    templateUrl: './correct-score-option-group.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./correct-score-option-group.component.scss'],
    imports: [
        MarketHelpComponent,
        NgClass,
        RxFor,
        NgTemplateOutlet,
        ShowInEpcotDirective,
        OptionComponent,
        IconCustomComponent,
        CorrectScoreOptionPipe,
    ],
})
export class CorrectScoreOptionGroupComponent implements OnChanges {
    sitecore = inject(Sitecore);
    sitecoreEventDetails = inject(SitecoreEventDetailsConfig);
    private correctScoreService = inject(CorrectScoreService);
    private trackingService = inject(TrackingService);
    private epcotConfig = inject(EpcotConfig);
    private strategyProvider = inject(RxStrategyProvider);
    private marketGroupingConfig = inject(MarketGroupingConfig);
    private usCorrectScoreService = inject(USCorrectScoreService);

    @Input() event: EventModel;
    @Input() tracking: string;
    @Input() trackingModuleType = '';
    @Input() isShowMoreDisabled: boolean;
    @Input() showMarketHelp: boolean;
    @Input() crmPromotedOptionIdsMap: StringDictionary<number> = {};
    @Input() eventHelpText?: string;
    @Input() sourceOfPick = SourceOfPick.Other;
    @Input() betDetails = '';
    @Input() optionsLimitOverride?: number;
    readonly optionGroup = input.required<EventOptionGroup>();

    private _isShowMoreDisabled = signal<boolean>(false);
    private correctScoreThreshold = this.marketGroupingConfig.showMoreCorrectScoreThreshold;
    private showMoreRowsThreshold = signal(this.correctScoreThreshold);

    optionsLayout = computed(() => {
        const isUSCorrectScore = this.isUSCorrectScoreTemplate();

        if (!this.optionGroup()) {
            return { layout: 1, options: [], rest: [] };
        }

        return isUSCorrectScore
            ? this.usCorrectScoreService.getUSCorrectScoreOptions(this.optionGroup())
            : this.correctScoreService.getCorrectScoreOptions(this.optionGroup());
    });

    readonly isUSCorrectScoreTemplate = computed(() => String(this.optionGroup().detailedGrouping.displayType) === OptionGroupType.USCorrectScore);

    scoreRowsCount: Signal<number> = computed(() => {
        return this.scoreOptions().length / this.optionsLayout().layout;
    });

    showAllOptions = computed(() => {
        const scoreRowsCount = this.optionsLayout().options.length / this.optionsLayout().layout;
        const isNotExceedingThreshold = scoreRowsCount + this.optionsLayout().rest.length <= this.showMoreRowsThreshold();

        return this._isShowMoreDisabled() || isNotExceedingThreshold;
    });

    // renderCallback
    readonly groupsRendered$ = new Subject<Nullable<EventOption>[]>();
    // strategy input
    readonly renderStrategy$ = new BehaviorSubject<string>(this.strategyProvider.primaryStrategy);

    private readonly scoreOptions = computed(() => {
        if (!this.isCollapsed() || this.showAllOptions()) {
            return this.optionsLayout().options;
        }
        const threshold = this.showMoreRowsThreshold() * this.optionsLayout().layout;

        return this.optionsLayout().options.slice(0, threshold);
    });

    scoreOptions$ = toObservable(this.scoreOptions).pipe(
        switchMap((options) => {
            if (this.startFresh) {
                this.startFresh = false;
                UnpatchedPromise.resolve().then(() => this.renderStrategy$.next('native'));

                return this.groupsRendered$.pipe(
                    take(1),
                    map(() => options),
                    tap(() => this.renderStrategy$.next(this.strategyProvider.primaryStrategy)),
                    startWith([]),
                );
            }

            return of(options);
        }),
    );

    restOptions$ = toObservable(
        computed(() => {
            if (!this.isCollapsed() || this.showAllOptions()) {
                return this.optionsLayout().rest;
            }
            const threshold = this.showMoreRowsThreshold() - this.scoreRowsCount();

            return this.optionsLayout().rest.slice(0, threshold);
        }),
    );

    scoreOptionsClass = computed(() => (this.optionsLayout().layout === 2 ? 'double' : 'triple'));

    isCollapsed = signal(true);

    private sitecoreReady = toSignal(this.sitecoreEventDetails.whenReady.pipe(map(() => true)), { initialValue: false });

    headerNames = computed(() => {
        const isThreeColumnLayout = this.optionsLayout().layout === 3;
        if (!this.sitecoreReady()) {
            return [];
        }

        if (this.isUSCorrectScoreTemplate()) {
            const team1 = this.getTeamName(EventParticipantType.Home);
            const team2 = this.getTeamName(EventParticipantType.Away);

            if (team1 && team2) {
                const interpolateTeam = (team: string) => interpolateSingleBracesString(this.sitecore.event.ToWin, { team });
                return isThreeColumnLayout
                    ? [interpolateTeam(team1), this.sitecoreEventDetails.eventDetails.Tie, interpolateTeam(team2)]
                    : [interpolateTeam(team1), interpolateTeam(team2)];
            }
        }

        const headers = ['1', '2'];
        if (isThreeColumnLayout) {
            if (this.epcotConfig.isEnabled) {
                headers.splice(1, 0, this.sitecoreEventDetails.eventDetails.Tie);
            } else {
                headers.splice(1, 0, 'X');
            }
        }

        return headers;
    });

    private startFresh = false;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.optionGroup) {
            if (!changes.optionGroup.firstChange && changes.optionGroup.previousValue?.id !== this.optionGroup().id) {
                this.startFresh = true;
            }
        }

        if (changes.optionsLimitOverride) {
            this.showMoreRowsThreshold.set(this.optionsLimitOverride ?? this.correctScoreThreshold);
        }

        if (changes.isShowMoreDisabled) {
            this._isShowMoreDisabled.set(this.isShowMoreDisabled);
        }
    }

    expand(): void {
        this.isCollapsed.set(!this.isCollapsed());

        this.trackingService.track(trackingConstants.EVENT_CLICKS, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'Competition Page Interactions',
            [trackingConstants.COMPONENT_LABEL_EVENT]: 'Visible Selections',
            [trackingConstants.COMPONENT_ACTION_EVENT]: this.isCollapsed() ? 'Show More' : 'Show Less',
            [trackingConstants.COMPONENT_POSITION_EVENT]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: NOT_APPLICABLE,
        });
    }

    trackOption(index: number, option: Nullable<EventOption>): string {
        return option ? `${option.id}` : `_${index}`;
    }

    isPlaceholderShadowVisible(index: number): boolean {
        return index >= this.optionsLayout().options.length - 2;
    }

    private getTeamName(type: EventParticipantType): string | undefined {
        const team = this.event.participants.find((p) => p.type === type);
        return team?.shortName || team?.name;
    }
}
